/**
 * Free-play hub — choose Hold the Line (your deck) or Muster climb.
 */
import { createAudioEngine } from "../shared/audio";
import { cardFullscreen } from "../shared/cards/CardFullscreen";
import { createVerseCardFace } from "../shared/cards/cardFace";
import { loadCollection } from "../shared/collection";
import { keywordsForVerse } from "../prototypes/proclaim/game/cards/keywords";
import type { VerseDef } from "../prototypes/proclaim/game/cards/types";
import {
  formatCheckpointSummary,
  getResumeCheckpoint,
} from "../prototypes/proclaim/game/endlessCheckpoint";
import { formatSurviveTime, getEndlessBest } from "../prototypes/proclaim/game/endlessScore";
import {
  formatMusterSummary,
  getMusterBest,
  getMusterDeck,
  isMusterDefault,
  resetMusterDeck,
} from "../prototypes/proclaim/game/muster";
import { verseById } from "../prototypes/proclaim/data/verses";
import {
  getActiveDeckId,
  listDecks,
  loadDeck,
  setActiveDeck,
} from "../shared/deck/storage";

document.documentElement.dataset.app = "freeplay";
loadCollection();

const deckAudio = createAudioEngine({ musicContent: false });
cardFullscreen.setVoPreview({
  play: async (verseId) => {
    await deckAudio.resume();
    return deckAudio.playVerseVo(verseId);
  },
  stop: () => deckAudio.stopVerseVo(),
  isPlaying: (verseId) => deckAudio.isVerseVoPlaying(verseId),
});

function openVerseStudy(def: VerseDef): void {
  const speak = def.speakSeconds ?? 2.5;
  const kws = keywordsForVerse(def);
  const detailRows: { label: string; value: string }[] = [
    { label: "Speak", value: `${speak.toFixed(1)}s` },
    { label: "Kind", value: def.kind },
    { label: "Category", value: def.category },
  ];
  if (def.strongVs?.length) {
    detailRows.push({
      label: "Meets",
      value: def.strongVs
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(" / "),
    });
  }
  if (def.preferLayer) {
    detailRows.push({ label: "Layer", value: def.preferLayer });
  }
  if (def.lockLayer) {
    detailRows.push({ label: "Lock", value: def.lockLayer });
  }
  cardFullscreen.show({
    verseId: def.id,
    reference: def.reference,
    category: def.category,
    speakSeconds: speak,
    metaLine: `${def.category} · ${def.kind}`,
    keywords: kws.map((k) => ({
      id: k.id,
      label: k.label,
      reminder: k.reminder,
    })),
    textKjv: def.textKjv,
    proclaimText: def.proclaimText,
    effectSummary: def.effectSummary,
    detailRows,
    studyLayout: true,
  });
}

type Mode = "hold" | "muster";

const detailEl = document.getElementById("fp-detail")!;
const holdBtn = document.getElementById("mode-hold") as HTMLButtonElement;
const musterBtn = document.getElementById("mode-muster") as HTMLButtonElement;
const holdMeta = document.getElementById("hold-meta");
const musterMeta = document.getElementById("muster-meta");

let mode: Mode = parseModeFromUrl();

function parseModeFromUrl(): Mode {
  const q = new URLSearchParams(window.location.search).get("mode");
  return q === "muster" ? "muster" : "hold";
}

function setUrlMode(m: Mode): void {
  const url = new URL(window.location.href);
  if (m === "hold") url.searchParams.delete("mode");
  else url.searchParams.set("mode", m);
  window.history.replaceState({}, "", url.toString());
}

function stripCards(ids: string[]): HTMLElement {
  const strip = document.createElement("div");
  strip.className = "fp-strip";
  strip.setAttribute("aria-label", "Deck cards");

  const counts = new Map<string, number>();
  const order: string[] = [];
  for (const id of ids) {
    if (!counts.has(id)) order.push(id);
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  for (const id of order) {
    const def = verseById(id);
    const wrap = document.createElement(def ? "button" : "div");
    wrap.className = "fp-strip-card";
    wrap.title = def?.reference ?? id;
    if (def) {
      (wrap as HTMLButtonElement).type = "button";
      wrap.setAttribute("aria-label", `Study ${def.reference}`);
      wrap.addEventListener("click", () => openVerseStudy(def));
      const face = createVerseCardFace({
        verseId: def.id,
        reference: def.reference,
        category: def.category,
        speakSeconds: def.speakSeconds ?? 2.5,
        size: "deck",
      });
      wrap.appendChild(face);
    } else {
      const miss = document.createElement("div");
      miss.className = "fp-strip-card-miss";
      miss.textContent = id;
      wrap.appendChild(miss);
    }
    const n = counts.get(id) ?? 1;
    if (n > 1) {
      const qty = document.createElement("span");
      qty.className = "fp-strip-qty";
      qty.textContent = `×${n}`;
      wrap.appendChild(qty);
    }
    strip.appendChild(wrap);
  }
  return strip;
}

function renderHoldDetail(): void {
  const decks = listDecks();
  let activeId = getActiveDeckId();
  if (!decks.some((d) => d.id === activeId)) {
    activeId = decks[0]?.id ?? activeId;
  }
  const deck = loadDeck(activeId);
  const best = getEndlessBest("standard");
  const cp = getResumeCheckpoint("standard", deck);

  if (holdMeta) {
    holdMeta.textContent =
      best > 0
        ? `${deck.verseIds.length} cards · best ${formatSurviveTime(best)}`
        : `${deck.verseIds.length} cards · your loadout`;
  }

  detailEl.replaceChildren();

  const title = document.createElement("h2");
  title.className = "fp-detail-title";
  title.id = "fp-detail-title";
  title.textContent = "Hold the Line";

  const lead = document.createElement("p");
  lead.className = "fp-detail-lead";
  lead.textContent =
    "Your saved loadout. Score is time held on Standard. Long runs can unlock verse packs.";

  const lab = document.createElement("p");
  lab.className = "fp-section-label";
  lab.textContent = "Your deck";

  const row = document.createElement("div");
  row.className = "fp-deck-row";

  if (decks.length > 1) {
    const label = document.createElement("label");
    const labText = document.createElement("span");
    labText.className = "visually-hidden";
    labText.textContent = "Loadout";
    const select = document.createElement("select");
    select.id = "fp-deck-select";
    select.setAttribute("aria-label", "Choose loadout");
    for (const d of decks) {
      const opt = document.createElement("option");
      opt.value = d.id;
      opt.textContent = `${d.name} (${d.verseIds.length})`;
      if (d.id === activeId) opt.selected = true;
      select.appendChild(opt);
    }
    select.addEventListener("change", () => {
      const id = select.value;
      if (!id) return;
      setActiveDeck(id);
      render();
    });
    label.append(labText, select);
    row.appendChild(label);
  } else {
    const name = document.createElement("span");
    name.className = "fp-deck-name";
    name.textContent = deck.name;
    row.appendChild(name);
  }

  const strip = stripCards(deck.verseIds);

  if (cp) {
    const card = document.createElement("div");
    card.className = "fp-checkpoint";
    const kicker = document.createElement("span");
    kicker.className = "fp-checkpoint-label";
    kicker.textContent = "Checkpoint ready";
    const value = document.createElement("strong");
    value.className = "fp-checkpoint-value";
    value.textContent = formatCheckpointSummary(cp);
    const hint = document.createElement("span");
    hint.className = "fp-checkpoint-note";
    hint.textContent =
      "Resume restores the full board at this protocol. New run starts from the beginning.";
    card.append(kicker, value, hint);
    detailEl.append(title, lead, lab, row, strip, card);
  } else {
    detailEl.append(title, lead, lab, row, strip);
  }

  const actions = document.createElement("div");
  actions.className = cp ? "fp-actions fp-actions--split" : "fp-actions";

  const playHref = `/prototypes/proclaim/?mode=free&deck=${encodeURIComponent(deck.id)}`;

  if (cp) {
    const resume = document.createElement("a");
    resume.className = "btn primary";
    resume.href = `${playHref}&start=resume`;
    resume.textContent = `Resume · ${cp.waveLabel?.trim() || "checkpoint"}`;
    resume.id = "fp-start-hold";
    resume.title = "Restore the full board at the last stable protocol";

    const fresh = document.createElement("a");
    fresh.className = "btn secondary";
    fresh.href = `${playHref}&start=new`;
    fresh.textContent = "New run";
    fresh.id = "fp-start-hold-new";

    actions.append(resume, fresh);
  } else {
    const start = document.createElement("a");
    start.className = "btn primary";
    start.href = playHref;
    start.textContent = "Start run";
    start.id = "fp-start-hold";
    actions.appendChild(start);
  }

  const build = document.createElement("a");
  build.className = "btn secondary";
  build.href = "/deck/?from=freeplay";
  build.textContent = "Deck builder";

  actions.appendChild(build);
  detailEl.appendChild(actions);
}

function renderMusterDetail(): void {
  const muster = getMusterDeck();
  const best = getMusterBest("standard");

  if (musterMeta) {
    musterMeta.textContent =
      best > 0
        ? `${formatMusterSummary()} · best ${formatSurviveTime(best)}`
        : formatMusterSummary();
  }

  detailEl.replaceChildren();

  const title = document.createElement("h2");
  title.className = "fp-detail-title";
  title.id = "fp-detail-title";
  title.textContent = "Muster";

  const lead = document.createElement("p");
  lead.className = "fp-detail-lead";
  lead.textContent =
    "Start with a thin deck. It grows as you survive — keep a finding or cut a card at the end.";

  const lab = document.createElement("p");
  lab.className = "fp-section-label";
  lab.textContent = "Start deck";

  const row = document.createElement("div");
  row.className = "fp-deck-row";
  const name = document.createElement("span");
  name.className = "fp-deck-name";
  name.textContent = isMusterDefault() ? "Default starter" : "Your muster";
  const count = document.createElement("span");
  count.className = "fp-deck-count";
  count.textContent = isMusterDefault()
    ? "Same six verses as a new campaign"
    : `${muster.length} cards`;
  row.append(name, count);

  const strip = stripCards(muster);

  if (best > 0) {
    const note = document.createElement("p");
    note.className = "fp-note";
    note.innerHTML = `Best depth · <strong>${formatSurviveTime(best)}</strong>`;
    detailEl.append(title, lead, lab, row, strip, note);
  } else {
    detailEl.append(title, lead, lab, row, strip);
  }

  const actions = document.createElement("div");
  actions.className = "fp-actions";

  const start = document.createElement("a");
  start.className = "btn primary";
  start.href = "/prototypes/proclaim/?mode=muster";
  start.textContent = "Start run";
  start.id = "fp-start-muster";

  actions.appendChild(start);

  if (!isMusterDefault()) {
    const reset = document.createElement("button");
    reset.type = "button";
    reset.className = "btn secondary";
    reset.textContent = "Reset to default";
    reset.title = "Restore the default starter deck. Best depth is kept.";
    reset.addEventListener("click", () => {
      const ok = window.confirm(
        "Reset muster to the default starter deck?\n\nBest depth is kept. This only affects Muster mode.",
      );
      if (!ok) return;
      resetMusterDeck();
      render();
    });
    actions.appendChild(reset);
  }

  detailEl.appendChild(actions);
}

function syncModeButtons(): void {
  const hold = mode === "hold";
  holdBtn.classList.toggle("is-selected", hold);
  musterBtn.classList.toggle("is-selected", !hold);
  holdBtn.setAttribute("aria-pressed", hold ? "true" : "false");
  musterBtn.setAttribute("aria-pressed", hold ? "false" : "true");
}

function render(): void {
  syncModeButtons();
  if (mode === "muster") renderMusterDetail();
  else renderHoldDetail();
}

holdBtn.addEventListener("click", () => {
  mode = "hold";
  setUrlMode(mode);
  render();
});
musterBtn.addEventListener("click", () => {
  mode = "muster";
  setUrlMode(mode);
  render();
});

// Side meta labels
if (holdMeta) {
  const deck = loadDeck();
  const best = getEndlessBest("standard");
  holdMeta.textContent =
    best > 0
      ? `Your deck · best ${formatSurviveTime(best)}`
      : `Your deck · ${deck.verseIds.length} cards`;
}
if (musterMeta) {
  const best = getMusterBest("standard");
  musterMeta.textContent =
    best > 0
      ? `${formatMusterSummary()} · best ${formatSurviveTime(best)}`
      : formatMusterSummary();
}

render();
